import { Component, ChangeDetectionStrategy, DestroyRef, inject, signal, computed, effect, ViewChild, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbModal, NgbModalModule, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import qrcode from './qrcode.js';
import { SessionApiService } from './session-api.service';
import { downloadAgenda, downloadActa } from './session-pdf';
import { SocketService } from 'app/core/service/core/socket.service';
import { RequestService } from '../../services/requests.service';
import { ViewerComponent, DocViewerItem } from 'app/feature/management-document/components/viewer/viewer.component';
import { Global } from 'app/core/service/core/Global';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { PeriodService, periodKey } from 'app/feature/management/billing-management/services/period.service';

@Component({
  selector: 'app-session-page',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, NgbModalModule, ViewerComponent, PageHeaderComponent],
  templateUrl: './session-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class SessionPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(SessionApiService);
  private sockets = inject(SocketService);
  private requests = inject(RequestService);
  readonly periodsApi = inject(PeriodService);
  private modal = inject(NgbModal);
  private destroyRef = inject(DestroyRef);

  @ViewChild('newSessionModal') newSessionModal?: TemplateRef<unknown>;
  private modalRef?: NgbModalRef;

  session = signal<any>(null);
  sessions = signal<any[]>([]);
  error = signal('');
  busy = signal(false);
  voteMode = signal('DIGITAL');
  linkBpm = signal(false);
  placeName = signal('');
  periodId = signal<number | null>(null);
  selectedPeriodStartId = signal<number | null>(null);
  selectedPeriodEndId = signal<number | null>(null);
  private rangeReady = false;
  qr = signal('');
  docs = signal<DocViewerItem[]>([]);
  activeDoc = signal<string | number | null>(null);
  deductible = signal<number | null>(null);
  amount = signal<number | null>(null);
  paperUserId = signal<number | null>(null);
  paperDecision = signal<'APROBAR' | 'NO'>('APROBAR');
  private docKey = '';

  current = computed(() => {
    const room = this.session();
    if (!room) return null;
    return (room.items || []).find((item: any) => item.id === room.currentItemId) || room.items?.[0] || null;
  });

  creatablePeriods = computed(() =>
    this.periodsApi.billingPeriods()
      .filter((period) => ['ACTIVE', 'INACTIVE'].includes(String(period.statusSlug || '').toUpperCase()))
      .map((period) => ({
        ...period,
        label: `${period.displayName} · ${String(period.statusSlug).toUpperCase() === 'ACTIVE' ? 'Abierto' : 'Cerrado'}`
      }))
  );

  filteredEndPeriods = computed(() => {
    const start = this.periodsApi.billingPeriods().find((period) => period.id === this.selectedPeriodStartId());
    const startKey = periodKey(start);
    return this.periodsApi.billingPeriods().filter((period) => periodKey(period) >= startKey);
  });

  visibleSessions = computed(() => {
    const start = this.periodsApi.billingPeriods().find((period) => period.id === this.selectedPeriodStartId());
    const end = this.periodsApi.billingPeriods().find((period) => period.id === this.selectedPeriodEndId());
    if (!start || !end) return this.sessions();
    const from = periodKey(start);
    const to = periodKey(end);
    return this.sessions().filter((row) => {
      const key = row.period
        ? periodKey(row.period)
        : this.keyFromYearMonth(row.yearMonth);
      return key >= from && key <= to;
    });
  });

  canVote = computed(() => {
    const item = this.current();
    const room = this.session();
    if (!item || item.phase !== 'EN_VOTACION' || room?.voteMode === 'MANUAL') return false;
    const frozen: number[] = item.summary?.frozenVoterIds || [];
    return frozen.includes(room.myUserId);
  });

  constructor() {
    effect(() => {
      const item = this.current();
      this.deductible.set(item?.approvedDeductible ?? null);
      this.amount.set(item?.approvedAmount ?? null);
      const key = `${item?.id || ''}:${item?.phase || ''}`;
      if (key !== this.docKey) {
        this.docKey = key;
        this.loadDocs();
      }
    });

    effect(() => {
      const list = this.periodsApi.billingPeriods();
      if (!list.length || this.rangeReady) return;
      this.rangeReady = true;
      const open = list.find((period) => String(period.statusSlug).toUpperCase() === 'ACTIVE');
      const newest = list[0];
      const year = Number((open || newest).year);
      const ofYear = list
        .filter((period) => Number(period.year) === year)
        .sort((a, b) => periodKey(a) - periodKey(b));
      const january = ofYear.find((period) => Number(period.period) === 1) || ofYear[0];
      this.selectedPeriodStartId.set(january?.id ?? null);
      this.selectedPeriodEndId.set((open || ofYear[ofYear.length - 1])?.id ?? null);
    });

    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const token = params.get('token');
      const uuid = params.get('uuid');
      if (token) this.enter(token);
      else if (uuid) this.open(uuid);
      else this.home();
    });

    this.sockets.onSessionSync().pipe(takeUntilDestroyed()).subscribe((event) => {
      const uuid = this.session()?.uuid;
      if (event.uuid && event.uuid === uuid) this.open(uuid, true);
    });
  }

  home(): void {
    this.session.set(null);
    if (!this.periodsApi.periods().length) this.periodsApi.reload();
    this.busy.set(true);
    Promise.all([
      firstValueFrom(this.api.defaults()),
      firstValueFrom(this.api.list())
    ]).then(([defs, rows]) => {
      this.linkBpm.set(!!defs.linkBpm);
      this.sessions.set(rows || []);
    }).catch((err) => this.error.set(this.message(err)))
      .finally(() => this.busy.set(false));
  }

  openNew(): void {
    const open = this.creatablePeriods().find((period) => String(period.statusSlug).toUpperCase() === 'ACTIVE');
    this.periodId.set(open?.id ?? this.creatablePeriods()[0]?.id ?? null);
    this.placeName.set('');
    this.voteMode.set('DIGITAL');
    if (this.newSessionModal) {
      this.modalRef = this.modal.open(this.newSessionModal, { size: 'lg' });
    }
  }

  closeNew(): void {
    this.modalRef?.close();
  }

  backToList(): void {
    this.router.navigate(['/solicitudes/sesion']);
  }

  openRow(uuid: string): void {
    this.router.navigate(['/solicitudes/sesion', uuid]);
  }

  onStartPeriod(id: number): void {
    this.selectedPeriodStartId.set(id);
    const end = this.periodsApi.billingPeriods().find((period) => period.id === this.selectedPeriodEndId());
    const start = this.periodsApi.billingPeriods().find((period) => period.id === id);
    if (end && start && periodKey(end) < periodKey(start)) this.selectedPeriodEndId.set(id);
  }

  create(): void {
    if (!this.periodId()) {
      this.error.set('Seleccione el periodo de la sesión.');
      return;
    }
    this.busy.set(true);
    this.error.set('');
    firstValueFrom(this.api.create({
      voteMode: this.voteMode(),
      linkBpm: this.linkBpm(),
      placeName: this.placeName(),
      periodId: this.periodId()!
    })).then((room) => {
      this.closeNew();
      this.router.navigate(['/solicitudes/sesion', room.uuid]);
    }).catch((err) => this.error.set(this.message(err)))
      .finally(() => this.busy.set(false));
  }

  periodLabel(row: any): string {
    if (row?.period?.year) return `${row.period.year} - ${String(row.period.period).padStart(2, '0')}`;
    return row?.yearMonth || '';
  }

  periodState(row: any): string {
    const slug = String(row?.period?.statusSlug || '').toUpperCase();
    if (slug === 'ACTIVE') return 'Abierto';
    if (slug === 'INACTIVE') return 'Cerrado';
    return '';
  }

  private keyFromYearMonth(value: string): number {
    const [year, month] = String(value || '').split('-');
    const y = Number(year);
    const m = Number(month);
    if (!y || !m) return 0;
    return y * 100 + m;
  }

  enter(token: string): void {
    this.busy.set(true);
    firstValueFrom(this.api.join(token))
      .then((room) => this.router.navigate(['/solicitudes/sesion', room.uuid], { replaceUrl: true }))
      .catch((err) => this.error.set(this.message(err)))
      .finally(() => this.busy.set(false));
  }

  open(uuid: string, silent = false): void {
    if (!silent) this.busy.set(true);
    firstValueFrom(this.api.get(uuid)).then((room) => {
      this.session.set(room);
      this.sockets.joinSession(room.uuid);
      this.paintQr(room);
    }).catch((err) => this.error.set(this.message(err)))
      .finally(() => this.busy.set(false));
  }

  run(call: Promise<any>): void {
    this.busy.set(true);
    this.error.set('');
    call.then((room) => this.session.set(room))
      .catch((err) => this.error.set(this.message(err)))
      .finally(() => this.busy.set(false));
  }

  present(itemId: number): void {
    this.run(firstValueFrom(this.api.focus(this.session().uuid, itemId)));
  }

  openVote(): void {
    const item = this.current();
    if (item) this.run(firstValueFrom(this.api.openVote(this.session().uuid, item.id)));
  }

  cast(decision: 'APROBAR' | 'NO'): void {
    const item = this.current();
    if (item) this.run(firstValueFrom(this.api.vote(this.session().uuid, item.id, decision)));
  }

  savePaper(): void {
    const item = this.current();
    const userId = this.paperUserId();
    if (!item || !userId) return;
    this.run(firstValueFrom(this.api.paperVote(this.session().uuid, item.id, userId, this.paperDecision())));
  }

  mark(userId: number): void {
    this.run(firstValueFrom(this.api.markPresent(this.session().uuid, userId)));
  }

  saveAmounts(): void {
    const item = this.current();
    if (!item) return;
    const budget = Number(item.summary?.lines?.[0]?.budget || 0);
    const pct = Number(this.deductible() || 0);
    const net = budget > 0 ? Math.round(budget * (1 - pct / 100)) : Number(this.amount() || 0);
    this.amount.set(net);
    this.run(firstValueFrom(this.api.amounts(this.session().uuid, item.id, pct, net)));
  }

  closePoint(body: { outcome?: string; action?: string }): void {
    const item = this.current();
    if (item) this.run(firstValueFrom(this.api.closeItem(this.session().uuid, item.id, body)));
  }

  closeSession(): void {
    this.run(firstValueFrom(this.api.close(this.session().uuid)));
  }

  agenda(): void { downloadAgenda(this.session()); }
  acta(): void { downloadActa(this.session()); }

  missingNames(item: any): string {
    const names = (item?.pendingNames || []).map((person: any) => person.fullName);
    return names.length ? names.join(', ') : 'nadie';
  }

  money(value: unknown): string {
    return `$${new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(Number(value || 0))}`;
  }

  absentRoster(): any[] {
    const room = this.session();
    const present = new Set((room?.attendance || []).map((row: any) => row.userId));
    return (room?.roster || []).filter((row: any) => !present.has(row.userId));
  }

  private paintQr(room: any): void {
    const url = `${window.location.origin}${room.joinPath}`;
    const code = qrcode(0, 'M');
    code.addData(url);
    code.make();
    const count = code.getModuleCount();
    const scale = 6;
    const canvas = document.createElement('canvas');
    canvas.width = count * scale;
    canvas.height = count * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000';
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (code.isDark(row, col)) ctx.fillRect(col * scale, row * scale, scale, scale);
      }
    }
    this.qr.set(canvas.toDataURL('image/png'));
  }

  private loadDocs(): void {
    const item = this.current();
    const uuid = item && !item.grouped ? item.summary?.lines?.[0]?.requestUuid : null;
    if (!uuid || item.phase === 'EN_VOTACION') {
      this.docs.set([]);
      return;
    }
    firstValueFrom(this.requests.getRequestByUuid(uuid)).then((request: any) => {
      const bags = [
        ...(request?.claim?.documents || []),
        ...((request?.claim?.details || []).flatMap((detail: any) => detail.documents || [])),
        ...(request?.requestDocuments || [])
      ];
      const items: DocViewerItem[] = [];
      for (const doc of bags) {
        const file = doc.document || doc;
        const fileUuid = file.uuid || file.fileUuid;
        if (!fileUuid) continue;
        items.push({
          id: fileUuid,
          uuid: fileUuid,
          title: doc.type?.name || doc.typeName || file.fileName || 'Documento',
          fileName: file.fileName || file.name || null,
          mimeType: file.mimeType || null,
          imageUrl: `${Global.url}V1/documents/view/${fileUuid}`
        });
      }
      this.docs.set(items);
      this.activeDoc.set(items[0]?.id ?? null);
    }).catch(() => this.docs.set([]));
  }

  private message(err: any): string {
    return err?.error?.error || err?.message || 'No se pudo completar la acción.';
  }
}
